/**
 * Video Library — dealer management page. One explainer video per category
 * (Brakes, Tires, Battery, Alignment, Suspension, A/C, Filters, Fluids,
 * Wipers). Ships empty; dealer pastes an mp4 URL per category and the
 * customer "My Vehicle" page automatically shows it when no per-item video
 * exists. Also serves as the fallback for hotspot cards.
 */

import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import api, { formatError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Video, Save, Play } from "lucide-react";

const CATEGORIES = [
  { key: "brakes",     label: "Brakes" },
  { key: "tires",      label: "Tires" },
  { key: "battery",    label: "Battery" },
  { key: "alignment",  label: "Alignment" },
  { key: "suspension", label: "Suspension" },
  { key: "ac",         label: "A/C" },
  { key: "filters",    label: "Filters" },
  { key: "fluids",     label: "Fluids" },
  { key: "wipers",     label: "Wipers" },
];

export default function VideoLibraryPage() {
  const [library, setLibrary] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/videos/library").then((r) => setLibrary(r.data || {})).catch((e) => {
      toast.error(formatError(e.response?.data?.detail) || "Could not load library");
    });
  }, []);

  const setUrl = (k, v) => setLibrary((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/videos/library", library);
      setLibrary(data);
      toast.success("Video library saved");
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <DashboardShell>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div>
          <div className="font-mono-label text-muted-foreground">Customer Page Content</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-1">Video Library</h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-3xl leading-relaxed">
            One short explainer per service category. When a customer taps a
            hotspot on their <b>My Vehicle</b> page and no per-item video
            exists, the category video plays instead (labeled "Why this matters").
            Paste a public mp4 URL for each — leave blank to skip.
          </p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-navy hover:bg-navy/90 text-white sm:shrink-0" data-testid="videolib-save-btn">
          <Save className="w-4 h-4 mr-2" strokeWidth={2} />
          {saving ? "Saving…" : "Save library"}
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {CATEGORIES.map((c) => (
          <Card key={c.key} className="p-4" data-testid={`videolib-card-${c.key}`}>
            <div className="flex items-center gap-2 mb-2">
              <Video className="w-4 h-4 text-navy" strokeWidth={2} />
              <div className="font-display font-bold text-navy dark:text-blue-200">{c.label}</div>
            </div>
            <Input
              value={library[c.key] || ""}
              onChange={(e) => setUrl(c.key, e.target.value)}
              placeholder="https://…/brakes.mp4"
              className="mb-2"
              data-testid={`videolib-input-${c.key}`}
            />
            {library[c.key] && (
              <div className="relative bg-black rounded overflow-hidden" style={{ aspectRatio: "16/9" }}>
                <video src={library[c.key]} controls muted playsInline className="w-full h-full object-cover" data-testid={`videolib-preview-${c.key}`} />
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[0.6rem] font-mono uppercase tracking-widest text-white bg-black/60 flex items-center gap-1">
                  <Play className="w-2.5 h-2.5" /> Preview
                </div>
              </div>
            )}
          </Card>
        ))}
      </div>
    </DashboardShell>
  );
}
